"use client";

import { useState } from "react";
import Section from "@/components/section";
import { useNextSectionScroll } from "@/lib/hooks/use-next-section-scroll";
import { Icons } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { motion } from "src/lib/motion";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import Image from "next/image";
import { useTranslations } from "@/lib/hooks/use-translations";
import dataDeletionImage from "@/assets/images/data-deletion.png";
import dataDeletionIcon from "@/assets/images/data-deletion.svg";
import aiReadnessIcon from "@/assets/images/ai-readiness.svg";
import aiEnterpriseIcon from "@/assets/images/enterprise-ai.svg";
import dataWarehouseIcon from "@/assets/images/data-warehouse.svg";
import dashboardImage from "@/assets/images/dashboard.svg";
import bigdataImage from "@/assets/images/bigdata.svg";
import visualsImage from "@/assets/images/visuals.svg";
import containerImage from "@/assets/images/container.svg";

const imageMap = {
  "data-deletion": bigdataImage,
  "enterprise-ai": dashboardImage,
  "ai-readiness": visualsImage,
  "data-warehouse": containerImage
};

export default function UseCases({ locale = "en" }: { locale?: "de" | "en" | "zh-CN" } = {}) {
  const handleNextSection = useNextSectionScroll("use-cases");
  const { t, locale: currentLocale } = useTranslations();
  const [activeTab, setActiveTab] = useState("data-deletion");

  const getContactUrl = () => {
    const localeToUse = currentLocale || locale;
    if (localeToUse === "de") return "/de/kontakt";
    if (localeToUse === "zh-CN") return "/zh-CN/contact";
    return "/contact";
  };

  const categories = [
    { id: "data-deletion",icon: dataDeletionIcon, label: t('pages.home.use-cases.categories.0.label') },
    { id: "enterprise-ai",icon: aiEnterpriseIcon, label: t('pages.home.use-cases.categories.1.label') },
    { id: "ai-readiness", icon: aiReadnessIcon, label: t('pages.home.use-cases.categories.2.label') },
    { id: "data-warehouse", icon: dataWarehouseIcon, label: t('pages.home.use-cases.categories.3.label') }
  ];
  const contents = {
    "data-deletion": {
      title: t('pages.home.use-cases.contents.data-deletion.title'),
      description: t('pages.home.use-cases.contents.data-deletion.description'),
      features: [
        t('pages.home.use-cases.contents.data-deletion.features.0'),
        t('pages.home.use-cases.contents.data-deletion.features.1'),
        t('pages.home.use-cases.contents.data-deletion.features.2')
      ]
    },
    "enterprise-ai": {
      title: t('pages.home.use-cases.contents.enterprise-ai.title'),
      description: t('pages.home.use-cases.contents.enterprise-ai.description'),
      features: [
        t('pages.home.use-cases.contents.enterprise-ai.features.0'),
        t('pages.home.use-cases.contents.enterprise-ai.features.1'),
        t('pages.home.use-cases.contents.enterprise-ai.features.2')
      ]
    },
    "ai-readiness": {
      title: t('pages.home.use-cases.contents.ai-readiness.title'),
      description: t('pages.home.use-cases.contents.ai-readiness.description'),
      features: [
        t('pages.home.use-cases.contents.ai-readiness.features.0'),
        t('pages.home.use-cases.contents.ai-readiness.features.1'),
        t('pages.home.use-cases.contents.ai-readiness.features.2')
      ]
    },
    "data-warehouse": {
      title: t('pages.home.use-cases.contents.data-warehouse.title'),
      description: t('pages.home.use-cases.contents.data-warehouse.description'),
      icon: '',
      features: [
        t('pages.home.use-cases.contents.data-warehouse.features.0'),
        t('pages.home.use-cases.contents.data-warehouse.features.1'),
        t('pages.home.use-cases.contents.data-warehouse.features.2')
      ]
    }
  };

  const content = contents[activeTab as keyof typeof contents];

  return (
    <Section
      id="use-cases"
      className="bg-neutral-100 dark:bg-neutral-900"
      mainCta={{
        text: t('pages.home.hero.cta.getStarted'),
        href: getContactUrl(),
        icon: <Icons.logoIcon className="h-6 w-6" />
      }}
      largerTopGap
      secondaryCta={{
        text: t('pages.home.hero.cta.readMore'),
        onClick: handleNextSection
      }}
    >
      {/* Überschrift im Stil des Bildes */}
      <div className="text-center mb-8">
        <p className="text-sm text-primary font-mono font-medium tracking-wider uppercase mb-4">{t('pages.home.use-cases.title')}</p>
        <h2 className="text-4xl md:text-5xl font-bold break-words">{t('pages.home.use-cases.subtitle')}</h2>
      </div>

      {/* Tab-Navigation */}
      <div className="flex flex-wrap justify-center gap-3 mb-12">
        {categories.map((category) => (
          <Button
            key={category.id}
            variant={activeTab === category.id ? "default" : "outline"}
            className={cn(
              "rounded-full px-6 max-sm:w-fit flex items-center gap-2",
              activeTab === category.id 
                ? "bg-primary text-white border-[1px] border-transparent" 
                : "bg-neutral-100 dark:bg-neutral-900 hover:md:bg-primary/10"
            )}
            onClick={() => setActiveTab(category.id)}
          >
            <Image
                src={category.icon}
                alt="icon"
                width={50}
                height={50}
                priority
                className={cn(
                  "h-full w-auto",
                  activeTab === category.id 
                    ? "brightness-0 invert" 
                    : "brightness-0 invert-0"
                )}
              />
            {category.label}
          </Button>
        ))}
      </div>

      {/* Inhalt mit zwei Spalten */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-12 items-center max-w-6xl mx-auto">


        {/* Rechte Spalte: Screenshot */}
        <motion.div
          key={`${activeTab}-image`}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="relative max-lg:hidden"
        >
          <div className="w-full max-w-lg mx-auto rounded-lg overflow-hidden">
            <div className="w-full md:h-[400px] rounded-lg relative z-10 bg-neutral-100 dark:bg-neutral-900">
              <Image
                src={imageMap[activeTab as keyof typeof imageMap]}
                alt={content.title}
                width={600}
                height={400}
                className="w-auto h-full object-contain mix-blend-multiply dark:mix-blend-screen"
                priority
              />
            </div>
          </div>
        </motion.div>

        {/* Linke Spalte: Text und Features */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-4"
        >
          <h3 className="text-2xl md:text-3xl font-semibold">{content.title}</h3>
          <p className="text-muted-foreground text-lg">{content.description}</p>
          
          <div className="space-y-3 mt-5">
            {content.features.map((feature: string, index: number) => (
              <div key={index} className="flex items-start gap-3">
                <div className="mt-1 bg-primary/10 rounded-full p-1">
                  <Check className="h-4 w-4 text-primary" />
                </div>
                <p>{feature}</p>
              </div>
            ))}
          </div>
          
        </motion.div>
        
      </div>
    </Section>
  );
}
